// src/components/member/SoalReports.jsx
import React, { useEffect, useState } from "react";
import "./SharedMember.css";
import "./AdminPanel.css";
import "./SoalReports.css";
import AnnouncementAdmin from "./announcements/AnnouncementAdmin";

const API_BASE =
    process.env.REACT_APP_API_BASE ||
    (process.env.NODE_ENV === "production"
        ? "https://api.soaltriskelions.com"
        : "http://localhost:5000");

export default function SoalReports() {
    const [reports, setReports] = useState([]);
    const [newTitle, setNewTitle] = useState("");
    const [newFile, setNewFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [tab, setTab] = useState("reports"); // "reports" | "announcements"
    const token = localStorage.getItem("token");

    /* ========== LOAD ALL REPORTS ========== */
    const loadReports = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/reports`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();

            if (res.ok && data.ok) {
                setReports(data.files || []);
                setMessage("");
            } else {
                setMessage("⚠️ Failed to load reports.");
            }
        } catch (err) {
            console.error(err);
            setMessage("❌ Network error while loading reports.");
        }
    };

    useEffect(() => {
        loadReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ========== UPLOAD NEW REPORT ========== */
    const uploadNewReport = async (e) => {
        e.preventDefault();
        if (!newTitle.trim() || !newFile) {
            setMessage("⚠️ Please enter a title and select a file.");
            return;
        }

        const formData = new FormData();
        formData.append("file", newFile);
        formData.append("title", newTitle.trim());

        try {
            setSaving(true);
            setMessage("");

            const res = await fetch(`${API_BASE}/api/reports/upload`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            const data = await res.json();

            if (res.ok && data.ok) {
                setMessage("✅ Report uploaded successfully!");
                setNewTitle("");
                setNewFile(null);
                loadReports();
            } else {
                setMessage(data.error || "❌ Upload failed.");
            }
        } catch (err) {
            console.error("Upload error:", err);
            setMessage("❌ Network error during upload.");
        } finally {
            setSaving(false);
        }
    };

    /* ========== DELETE REPORT ========== */
    const deleteReport = async (filename) => {
        if (!window.confirm(`Delete report "${filename}"?`)) return;

        try {
            const res = await fetch(`${API_BASE}/api/reports/${filename}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();

            if (res.ok && data.ok) {
                setReports((prev) => prev.filter((r) => r.filename !== filename));
                setMessage("🗑️ Report deleted successfully.");
            } else {
                setMessage(data.error || "❌ Delete failed.");
            }
        } catch (err) {
            console.error("Delete error:", err);
            setMessage("❌ Network error while deleting.");
        }
    };

    /* ========== RENDER ========== */
    return (
        <div className="report-section">
            <h3>📄 Reports & Document Management</h3>

            {/* ====== Tabs ====== */}
            <div className="report-tabs" style={{ marginBottom: 12 }}>
                <button
                    className={`tab-btn ${tab === "reports" ? "active" : ""}`}
                    data-tooltip="Reports & Document Management"
                    onClick={() => setTab("reports")}
                >
                    Documents
                </button>
                <button
                    className={`tab-btn ${tab === "announcements" ? "active" : ""}`}
                    data-tooltip="Popup Message"
                    onClick={() => setTab("announcements")}
                >
                    Announcements
                </button>
            </div>

            {/* ====== REPORTS TAB ====== */}
            {tab === "reports" && (
                <>
                    {/* Upload Form */}
                    <form onSubmit={uploadNewReport} className="report-upload">
                        <div className="form-group">
                            <label htmlFor="reportTitle">Title:</label>
                            <input
                                id="reportTitle"
                                type="text"
                                placeholder="e.g. Ducoment Name"
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="reportFile">Upload File:</label>
                            <input
                                id="reportFile"
                                type="file"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                                onChange={(e) => setNewFile(e.target.files[0])}
                                required
                            />
                        </div>

                        <div className="report-actions">
                            <button type="submit" disabled={saving}>
                                {saving ? "Uploading..." : "Upload & Save"}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setNewTitle("");
                                    setNewFile(null);
                                    setMessage("");
                                }}
                            >
                                Clear
                            </button>
                        </div>
                    </form>

                    {/* Message Feedback */}
                    {message && (
                        <div
                            className="report-message"
                            style={{
                                marginTop: 10,
                                fontWeight: 600,
                                color: /✅|success/i.test(message)
                                    ? "#5ad67d"
                                    : /🗑️|deleted/i.test(message)
                                        ? "orange"
                                        : "#ff6b6b",
                            }}
                        >
                            {message}
                        </div>
                    )}

                    {/* Uploaded Reports List */}
                    <div className="report-list">
                        <h4>📂 Uploaded Reports</h4>
                        {reports.length === 0 ? (
                            <p>No reports uploaded yet.</p>
                        ) : (
                            <ul>
                                {reports.map((r, i) => (
                                    <li key={r.filename || i} className="report-item">
                                        <div className="report-info">
                                            <strong>{r.title || `Report ${i + 1}`}</strong>
                                            <div className="report-meta">
                                                <a
                                                    href={r.url || `${API_BASE}/reports/${r.filename}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {r.originalname}
                                                </a>
                                                <span className="report-date">
                                                    Uploaded: {new Date(r.uploadedat).toLocaleString()}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            className="delete-btn"
                                            onClick={() => deleteReport(r.filename)}
                                        >
                                            Delete
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}

            {/* ====== ANNOUNCEMENTS TAB ====== */}
            {tab === "announcements" && <AnnouncementAdmin />}
        </div>
    );
}
